import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api, { extractErr, mediaUrl } from "@/lib/api";
import { Mail, Phone, MessageCircle, Clock, MapPin, Star } from "lucide-react";
import SeoMeta from "@/components/SeoMeta";
import { useStoreBase } from "@/lib/store";
import { contactList } from "@/lib/contactFields";
import { toast } from "sonner";
import ContactPageForm from "./ContactPageForm";

function AboutPage({ page, primary }) {
  const d = page.data || {};
  const values = d.values || [];
  const stats = (d.stats || []).filter((s) => s.value || s.label);
  return (
    <div>
      {/* Hero */}
      <section className="relative overflow-hidden">
        {d.image_url ? (
          <div className="relative h-64 sm:h-80">
            <img src={mediaUrl(d.image_url)} alt="" className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-black/20" />
            <div className="relative h-full max-w-4xl mx-auto px-6 flex flex-col justify-end pb-8 text-white">
              <h1 className="font-display text-4xl sm:text-5xl font-bold drop-shadow">{d.heading || page.title}</h1>
              {d.subheading && <p className="mt-2 text-lg opacity-95 max-w-2xl drop-shadow">{d.subheading}</p>}
            </div>
          </div>
        ) : (
          <div className="py-14 text-center" style={{ background: `${primary}10` }}>
            <h1 className="font-display text-4xl sm:text-5xl font-bold text-stone-900">{d.heading || page.title}</h1>
            {d.subheading && <p className="mt-3 text-lg text-stone-600 max-w-2xl mx-auto px-6">{d.subheading}</p>}
          </div>
        )}
      </section>

      <div className="max-w-4xl mx-auto px-6 py-12 space-y-12">
        {stats.length > 0 && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            {stats.map((s, i) => (
              <div key={i} className="text-center p-5 rounded-2xl bg-white border border-stone-200/70">
                <div className="font-display text-3xl font-extrabold" style={{ color: primary }}>{s.value}</div>
                <div className="text-sm text-stone-500 mt-1">{s.label}</div>
              </div>
            ))}
          </div>
        )}

        {d.story && (
          <section>
            <h2 className="font-display text-2xl font-bold text-stone-900">Our story</h2>
            <p className="mt-4 text-stone-700 leading-relaxed whitespace-pre-wrap text-lg">{d.story}</p>
          </section>
        )}

        {d.mission && (
          <section className="rounded-3xl p-8 text-white" style={{ background: primary }}>
            <div className="text-xs uppercase tracking-[0.2em] font-bold opacity-80">Our mission</div>
            <p className="mt-3 text-xl sm:text-2xl font-display font-semibold leading-snug whitespace-pre-wrap">{d.mission}</p>
          </section>
        )}

        {values.length > 0 && (
          <section>
            <h2 className="font-display text-2xl font-bold text-stone-900">What we stand for</h2>
            <div className="mt-6 grid sm:grid-cols-2 gap-5">
              {values.map((v, i) => (
                <div key={i} className="p-6 rounded-2xl bg-white border border-stone-200/70">
                  <div className="w-10 h-10 rounded-full flex items-center justify-center mb-3" style={{ background: `${primary}18`, color: primary }}><Star className="w-5 h-5" /></div>
                  <div className="font-display font-bold text-stone-900 text-lg">{v.title}</div>
                  {v.description && <p className="mt-1.5 text-stone-600 leading-relaxed">{v.description}</p>}
                </div>
              ))}
            </div>
          </section>
        )}

        {!d.story && !d.mission && values.length === 0 && stats.length === 0 && (
          <p className="text-center text-stone-500">This page hasn&#39;t been set up yet.</p>
        )}
      </div>
    </div>
  );
}

function ContactPage({ page, primary, config, slug }) {
  const d = page.data || {};
  const emails = contactList(d, "emails", "email").map((x) => x.trim()).filter(Boolean);
  const phones = contactList(d, "phones", "phone").map((x) => x.trim()).filter(Boolean);
  const items = [
    ...emails.map((email) => ({ icon: Mail, label: "Email", value: email, href: `mailto:${email}` })),
    ...phones.map((phone) => ({ icon: Phone, label: "Call us", value: phone, href: `tel:${phone}` })),
    d.whatsapp && { icon: MessageCircle, label: "WhatsApp", value: d.whatsapp, href: `https://wa.me/${(d.whatsapp || "").replace(/[^0-9]/g, "")}` },
    d.hours && { icon: Clock, label: "Hours", value: d.hours, href: null },
  ].filter(Boolean);
  return (
    <div className="max-w-4xl mx-auto px-6 py-12">
      <div className="text-center">
        <h1 className="font-display text-4xl sm:text-5xl font-bold text-stone-900">{d.heading || page.title}</h1>
        {d.subheading && <p className="mt-3 text-lg text-stone-600">{d.subheading}</p>}
        <ContactPageForm config={config} slug={slug} primary={primary} />
      </div>

      <div className="mt-10 grid sm:grid-cols-2 gap-4">
        {items.map((it, i) => {
          const Inner = (
            <div className="flex items-start gap-4 p-5 rounded-2xl bg-white border border-stone-200/70 h-full">
              <div className="w-11 h-11 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: `${primary}18`, color: primary }}><it.icon className="w-5 h-5" /></div>
              <div>
                <div className="text-xs uppercase tracking-wide text-stone-400 font-semibold">{it.label}</div>
                <div className="text-stone-900 font-medium mt-0.5 break-words">{it.value}</div>
              </div>
            </div>
          );
          return it.href ? <a key={`${it.label}-${it.value}-${i}`} href={it.href} target="_blank" rel="noopener noreferrer" data-testid={`contact-${it.label}`}>{Inner}</a> : <div key={`${it.label}-${i}`}>{Inner}</div>;
        })}
      </div>

      {d.address && (
        <div className="mt-4 flex items-start gap-4 p-5 rounded-2xl bg-white border border-stone-200/70">
          <div className="w-11 h-11 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: `${primary}18`, color: primary }}><MapPin className="w-5 h-5" /></div>
          <div>
            <div className="text-xs uppercase tracking-wide text-stone-400 font-semibold">Visit us</div>
            <div className="text-stone-900 font-medium mt-0.5 whitespace-pre-wrap">{d.address}</div>
          </div>
        </div>
      )}

      {d.map_embed && (
        <div className="mt-6 rounded-2xl overflow-hidden border border-stone-200">
          <iframe title="Map" src={d.map_embed} className="w-full h-72" style={{ border: 0 }} loading="lazy" referrerPolicy="no-referrer-when-downgrade" />
        </div>
      )}

      {items.length === 0 && !d.address && <p className="mt-8 text-center text-stone-500">Contact details haven&#39;t been added yet.</p>}
    </div>
  );
}

function FaqPage({ page, primary, slug }) {
  const d = page.data || {};
  const sections = Array.isArray(d.sections) ? d.sections : [];
  const [open, setOpen] = useState({});
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post(`/store/${slug}/faq-contact`, form);
      setDone(true);
    } catch (err) {
      toast.error(extractErr(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10 sm:py-14" data-testid="faq-page">
      <div className="text-center max-w-3xl mx-auto">
        <h1 className="font-display text-4xl sm:text-5xl font-bold text-stone-900">{d.heading || page.title || "FAQ's"}</h1>
        {d.intro ? <p className="mt-3 text-sm sm:text-base text-stone-600 leading-relaxed">{d.intro}</p> : null}
      </div>
      <div className="mt-10 grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_300px] gap-6 lg:gap-8 items-start">
        <div className="space-y-6 min-w-0">
          {sections.length === 0 ? <p className="text-center text-stone-500">Questions will appear here once they are added.</p> : null}
          {sections.map((sec) => (
            <section key={sec.id} className="rounded-2xl border border-stone-200 bg-white px-4 sm:px-8 py-6 sm:py-8" data-testid="faq-section">
              <h2 className="text-center font-display text-2xl font-bold text-stone-900">{sec.title}</h2>
              {sec.description ? <p className="mt-1 text-center text-sm text-stone-500">{sec.description}</p> : null}
              <div className="mt-6 divide-y divide-stone-200">
                {(sec.items || []).map((row) => {
                  const on = Boolean(open[row.id]);
                  return (
                    <div key={row.id}>
                      <button
                        type="button"
                        className="w-full flex items-center justify-between gap-4 py-4 text-left text-sm sm:text-[15px] font-semibold text-stone-900"
                        data-testid="faq-question"
                        aria-expanded={on}
                        onClick={() => setOpen((cur) => ({ ...cur, [row.id]: !cur[row.id] }))}
                      >
                        <span>{row.question}</span>
                        <span className="text-xl leading-none text-stone-500 shrink-0" aria-hidden="true">{on ? "−" : "+"}</span>
                      </button>
                      {on && row.answer ? <p className="pb-4 text-sm text-stone-600 leading-relaxed whitespace-pre-wrap" data-testid="faq-answer">{row.answer}</p> : null}
                    </div>
                  );
                })}
              </div>
            </section>
          ))}
        </div>
        <aside className="lg:sticky lg:top-24" data-testid="faq-contact-card">
          <div className="rounded-2xl border border-stone-200 bg-white p-5 sm:p-6">
            <h2 className="font-display text-xl font-bold text-stone-900">{d.form_heading || "Didn't find your answer?"}</h2>
            <p className="mt-1 text-sm text-stone-500">{d.form_subtext || "Don't hesitate to contact us"}</p>
            {done ? (
              <p className="mt-6 text-sm text-emerald-700" data-testid="faq-contact-thanks">Thanks, we received your message.</p>
            ) : (
              <form className="mt-5 space-y-3" onSubmit={submit} data-testid="faq-contact-form">
                <input className="w-full rounded-md border border-stone-300 px-3 py-2 text-sm" placeholder="Name" data-testid="faq-contact-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
                <input required type="email" className="w-full rounded-md border border-stone-300 px-3 py-2 text-sm" placeholder="Email" data-testid="faq-contact-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
                <textarea required rows={4} className="w-full rounded-md border border-stone-300 px-3 py-2 text-sm" placeholder="Message" data-testid="faq-contact-message" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} />
                <button type="submit" disabled={busy} className="inline-flex items-center rounded-full px-5 py-2 text-sm font-semibold text-white disabled:opacity-60" style={{ background: primary }} data-testid="faq-contact-submit">
                  {busy ? "Sending…" : "Send message"}
                </button>
              </form>
            )}
          </div>
        </aside>
      </div>
    </div>
  );
}

function AccountDeletionPage({ page, primary, slug }) {
  const d = page.data || {};
  const [form, setForm] = useState({ name: "", email: "", phone: "", reason: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post(`/store/${slug}/account-deletion`, form);
      setDone(true);
    } catch (err) {
      toast.error(extractErr(err));
    } finally {
      setBusy(false);
    }
  };
  if (done) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-12 text-center" data-testid="acctdel-thanks">
        <h1 className="font-display text-4xl font-bold text-stone-900">Request received</h1>
        <p className="mt-3 text-lg text-stone-600">We will process your account deletion request and contact you at the email you provided.</p>
      </div>
    );
  }
  return (
    <div className="max-w-3xl mx-auto px-6 py-12" data-testid="acctdel-page">
      <h1 className="font-display text-4xl sm:text-5xl font-bold text-stone-900">{d.heading || page.title}</h1>
      {d.subheading && <p className="mt-3 text-lg text-stone-600">{d.subheading}</p>}
      {d.instructions && <p className="mt-8 text-stone-700 leading-relaxed whitespace-pre-wrap">{d.instructions}</p>}
      {d.processing_note && <p className="mt-4 text-sm text-stone-500">{d.processing_note}</p>}
      <form className="mt-10 space-y-4 rounded-2xl border border-stone-200 bg-white p-6" onSubmit={submit} data-testid="acctdel-form">
        <div>
          <label className="text-sm font-medium text-stone-700">Full name</label>
          <input className="mt-1 w-full rounded-lg border border-stone-200 px-3 py-2" data-testid="acctdel-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
        </div>
        <div>
          <label className="text-sm font-medium text-stone-700">Email used on this store *</label>
          <input required type="email" className="mt-1 w-full rounded-lg border border-stone-200 px-3 py-2" data-testid="acctdel-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
        </div>
        <div>
          <label className="text-sm font-medium text-stone-700">Phone</label>
          <input className="mt-1 w-full rounded-lg border border-stone-200 px-3 py-2" data-testid="acctdel-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
        </div>
        <div>
          <label className="text-sm font-medium text-stone-700">Reason (optional)</label>
          <textarea rows={4} className="mt-1 w-full rounded-lg border border-stone-200 px-3 py-2" data-testid="acctdel-reason" value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} />
        </div>
        <button type="submit" disabled={busy} className="w-full rounded-xl py-3 font-semibold text-white disabled:opacity-60" style={{ background: primary }} data-testid="acctdel-submit">
          {busy ? "Sending…" : "Submit deletion request"}
        </button>
      </form>
    </div>
  );
}

export default function StorefrontPage({ config }) {
  const { pageSlug } = useParams();
  const { slug, base } = useStoreBase();
  const [p, setP] = useState(null);
  const primary = config.branding?.primary_color || "#15803D";
  useEffect(() => { api.get(`/store/${slug}/pages/${pageSlug}`).then(({ data }) => setP(data)).catch(() => setP(false)); }, [slug, pageSlug]);

  if (p === false) return <div className="p-16 text-center text-stone-500">Page not found</div>;
  if (!p) return <div className="p-16 text-center text-stone-500">Loading…</div>;

  return (
    <>
      <SeoMeta endpoint={`/seo/${slug}/meta`} fallback={{ title: `${p.title} · ${config.store_info?.name || slug}` }} />
      {pageSlug === "about" ? (
        <AboutPage page={p} primary={primary} />
      ) : pageSlug === "contact" ? (
        <ContactPage page={p} primary={primary} config={config} slug={slug} />
      ) : pageSlug === "faq" ? (
        <FaqPage page={p} primary={primary} slug={slug} />
      ) : pageSlug === "account-deletion" ? (
        <AccountDeletionPage page={p} primary={primary} slug={slug} />
      ) : (
        <div className="max-w-3xl mx-auto px-6 py-10">
          <h1 className="font-display text-3xl sm:text-4xl font-bold text-stone-900">{p.title}</h1>
          <div className="mt-6 whitespace-pre-wrap text-stone-700 leading-relaxed" data-testid={`page-content-${pageSlug}`}>{p.content || "This page hasn't been set up yet."}</div>
        </div>
      )}
    </>
  );
}
